import React, { useMemo } from 'react';

import config from 'config';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import ScrollOffset from '~/shared/components/ScrollOffset';
import SizeFabricGuideModal from '~/shared/components/SizeFabricGuideModal';
import { isIneligibleFitGuideProduct } from '~/shared/utils/isIneligibleFitGuideProduct';
import { getKidsProductCategories } from '~/shared/utils/productCategoryHelpers';
import { shouldUseAlternateSizeGuide } from '~/shared/utils/shouldUseAlternateSizeGuide';
import { usePDPOptionLabels } from '~/shared/utils/useLabels';
import useMysteryBoxConfig from '~/shared/utils/useMysteryBoxConfig';
import useProductDetailEvents from '~/shared/utils/useProductDetailEvents';
import SizeFabricGuideFableticsModal from '~/staticPages/components/SizeFabricGuideFableticsModal';
import {
  mobile,
  useBreakpoint,
  OptionLabelProvider,
  useProductDetailContext,
  Separator,
  SkeletonLine,
} from '~/techstyle-shared/react-components';
import { FormattedMessage } from '~/techstyle-shared/react-intl';

import BuilderSizeGuideModal from '../BuilderSizeGuideModal';
import HotComment from '../HotComment';
import MysteryBoxSizeSection from '../MysteryBoxSizeSection';
import MysteryBoxStyleSection from '../MysteryBoxStyleSection';
import OutfitItems from '../OutfitItems';
import ProductDetailColorSelector from '../ProductDetailColorSelector';
import { useProductDetailEmbroideryContext } from '../ProductDetailCTAs/ProductDetailEmbroideryContext';
import ProductDetailEmbroiderySelector from '../ProductDetailEmbroiderySelector.js';
import ProductDetailOtherSelectors from '../ProductDetailOtherSelectors';
import ProductDetailPrice from '../ProductDetailPrice';
import ProductDetailQuantitySelector from '../ProductDetailQuantitySelector';
import ProductDetailSizeSelector from '../ProductDetailSizeSelector';
import { ProductDescription as BaseProductDescription } from '../ProductDetailsSection';
import ScrubsSizeGuideModal from '../ScrubsSizeGuideModal';

const ItemWrapper = styled.div`
  ${mobile`
    padding: 24px 24px 0 24px;
  `}
`;

const OutfitSection = styled.div`
  ${mobile`
    background: ${({ theme }) => theme.colors.pdpOutfitSectionBg};
    margin-bottom: 16px;
    padding: 16px;
  `}
`;

const OutfitMessageWrapper = styled.div`
  background: ${({ theme }) => theme.colors.palette.white};

  ${mobile`
    border-bottom: 1px solid ${({ theme }) =>
      theme.colors.productDetailsOutfitMessage};
  `}
`;

const OutfitItemCountMessage = styled.div`
  margin-bottom: 16px;
  text-align: center;
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.sublabelBlack};

  ${mobile`
    margin-top: 16px;
  `}
`;

const separatorStyle = css`
  margin: 16px 0px;
  color: ${({ theme }) => theme.colors.productDetailSeparator};
`;

const mysteryBoxSeparatorStyle = css`
  margin: 21px 0 0;
`;

const ProductDescription = styled(BaseProductDescription)`
  padding: 0 16px;
`;

const MysteryBoxHeading = styled.div`
  color: ${({ theme }) => theme.colors.mysteryBoxLabel};
  font-size: 18px;
  font-weight: 500;
  margin: 21px 16px 8px;
`;

const MysteryBoxSubHeading = styled.div`
  color: ${({ theme }) => theme.colors.mysteryBoxSubLabel};
  font-size: 12px;
  font-weight: 400;
  margin: 0 16px;
`;

const SkeletonLineWithMarginBottom = styled(SkeletonLine)`
  margin-bottom: ${(props) =>
    props.$marginBottom ? `${props.$marginBottom}px` : 0};
`;

const MysteryBoxQuantitySection = styled.div`
  ${mobile`
    padding: 0 24px;
  `}
`;

// this is currently a savage only component, if we ever need this for other brands,
// we should move these styles to typography or theme tokens.
const SXFSizeGuideCalloutWrapper = styled.div`
  font-size 12px;
  font-weight 300;
  letter-spacing: 0.3px;
  line-height: 1.2;
  color: ${({ theme }) => theme.colors.sublabelBlack};
  margin-top: 16px;
  margin-bottom: 8px;
`;

const LineLoaderWithContainer = (props) => {
  return (
    <div>
      <SkeletonLineWithMarginBottom {...props} />
    </div>
  );
};

export const ProductDetailsLoading = () => {
  return (
    <>
      <LineLoaderWithContainer height={20} $marginBottom={12} width={200} />
      <LineLoaderWithContainer height={50} $marginBottom={24} />
      <LineLoaderWithContainer height={20} $marginBottom={12} width={75} />
      <LineLoaderWithContainer height={50} $marginBottom={10} />
      <LineLoaderWithContainer height={20} $marginBottom={32} width={200} />
      <LineLoaderWithContainer height={20} $marginBottom={6} width={50} />
      <LineLoaderWithContainer height={20} $marginBottom={12} width={200} />
      <LineLoaderWithContainer height={75} $marginBottom={24} />
    </>
  );
};

const scrubsId = config.get('public.productCategories.scrubs');
const showSXFSizeGuideCallout = config.get(
  'public.brand.features.showSXFSizeGuideCallout'
);

const shouldShowSizeGuideIcon = config.get(
  'public.brand.features.showSizeGuideIcon'
);

const sizeGuideButtonStyle = css`
  color: ${({ theme }) => theme.colors.sublabelBlack};
  font-weight: 600;
`;

const SizeGuide = ({
  builderSizeGuide,
  isScrubs,
  categoryIdList,
  showFitGuide,
  tagIdList,
}) => {
  const kidsIds = getKidsProductCategories();
  const isKids = categoryIdList?.some((categoryId) =>
    kidsIds?.includes(categoryId)
  );
  const petID = config.get('public.productTags.pets');
  const isPets = tagIdList?.includes(petID);
  const useAlternateSizeGuide = shouldUseAlternateSizeGuide(tagIdList);
  const useFableticsPDPFitGuide = config.get(
    'public.brand.features.useFableticsPDPFitGuide'
  );
  const showFableticsFitGuide = useFableticsPDPFitGuide || isKids;

  if (isScrubs) {
    return (
      <ScrubsSizeGuideModal
        categoryIdList={categoryIdList}
        showMessaging
        showSizeCalloutMessage={false}
        useAlternate={useAlternateSizeGuide}
      />
    );
  }
  if (builderSizeGuide) {
    return (
      <BuilderSizeGuideModal
        builderContent={builderSizeGuide}
        sizeGuideButtonStyle={sizeGuideButtonStyle}
      />
    );
  }

  if (showFitGuide && !showFableticsFitGuide) {
    return (
      <SizeFabricGuideModal
        useAlternate={useAlternateSizeGuide}
        shouldShowAssociatedIcon={shouldShowSizeGuideIcon}
      />
    );
  }

  if (showFitGuide && showFableticsFitGuide) {
    return (
      <SizeFabricGuideFableticsModal
        categoryIdList={categoryIdList}
        useAlternate={useAlternateSizeGuide}
        isKids={isKids}
        isPets={isPets}
      />
    );
  }

  return null;
};

SizeGuide.propTypes = {
  builderSizeGuide: PropTypes.object,
  categoryIdList: PropTypes.arrayOf(PropTypes.number),
  isScrubs: PropTypes.bool,
  showFitGuide: PropTypes.bool,
  tagIdList: PropTypes.arrayOf(PropTypes.number),
};

export default function ProductDetails({
  productDetail: productDetailFromProps,
  productRef,
  relatedProductsLoading,
}) {
  const productDetailFromCtx = useProductDetailContext();
  const productDetail = productDetailFromProps || productDetailFromCtx;
  const product = productDetail.product;
  const builderSizeGuide = productDetail.builderSizeGuide;
  const numberOfPieces = product?.bundleComponentProducts?.length;
  const categoryIdList = product?.productCategoryIdList;
  const tagIdList = product?.tagIdList;
  const description = product?.description;
  const isMysteryBox = product?.isMysteryBundle;
  const mysteryBoxConfig = useMysteryBoxConfig({
    masterProductId: product?.masterProductId,
    permalink: product?.permalink,
    enabled: !!isMysteryBox,
  });
  const isScrubs = categoryIdList?.includes(scrubsId);
  const showFitGuide =
    !productDetail.isBundle &&
    !isIneligibleFitGuideProduct(product) &&
    !isScrubs;
  useProductDetailEvents();
  const hasSXFSizeGuideCallout = useMemo(() => {
    return (
      showSXFSizeGuideCallout &&
      tagIdList?.includes(
        config.get('public.productTags.hasSXFSizeGuideCallout')
      )
    );
  }, [tagIdList]);
  const { isMobile } = useBreakpoint();
  const { showEmbroiderySelector } = useProductDetailEmbroideryContext();

  return (
    <OptionLabelProvider useLabels={usePDPOptionLabels}>
      {productDetail.isBundle ? (
        <>
          {!isMysteryBox && (
            <>
              {description ? (
                <ProductDescription description={description} />
              ) : null}
              <OutfitMessageWrapper>
                <OutfitItemCountMessage>
                  <FormattedMessage
                    id="site_product_page.outfit_includes"
                    defaultMessage="This {1}-Piece Outfit Includes:"
                    values={{
                      1: numberOfPieces,
                    }}
                  />
                </OutfitItemCountMessage>
              </OutfitMessageWrapper>

              <ScrollOffset ref={productRef} offset={10}>
                <OutfitSection data-testid="pdp-outfits-section">
                  <OutfitItems />
                </OutfitSection>
                {isMobile && (
                  <ItemWrapper>
                    <ProductDetailPrice />
                  </ItemWrapper>
                )}
              </ScrollOffset>
            </>
          )}
          {isMysteryBox && mysteryBoxConfig && (
            <>
              {isMobile && (
                <>
                  {mysteryBoxConfig.heading && (
                    <MysteryBoxHeading>
                      {mysteryBoxConfig.heading}
                    </MysteryBoxHeading>
                  )}
                  {mysteryBoxConfig.subHeading && (
                    <MysteryBoxSubHeading>
                      {mysteryBoxConfig.subHeading}
                    </MysteryBoxSubHeading>
                  )}
                  <Separator
                    thickness={1}
                    separatorStyle={mysteryBoxSeparatorStyle}
                    color="mysteryBoxDividerBorder"
                  />
                </>
              )}
              <MysteryBoxSizeSection config={mysteryBoxConfig} />
              <MysteryBoxStyleSection config={mysteryBoxConfig} />
              <MysteryBoxQuantitySection>
                <ProductDetailQuantitySelector />
              </MysteryBoxQuantitySection>
            </>
          )}
        </>
      ) : (
        <ScrollOffset ref={productRef} offset={10}>
          <ItemWrapper>
            <ProductDetailColorSelector
              relatedProductsLoading={relatedProductsLoading}
              displayLoadingState
              disableAllOptions={productDetail.isAssortmentTestProductDisabled}
            />
            <ProductDetailSizeSelector
              disableAllOptions={productDetail.isAssortmentTestProductDisabled}
            />
            {hasSXFSizeGuideCallout && (
              <SXFSizeGuideCalloutWrapper>
                <FormattedMessage
                  id="site_pdp.size_guide_callout"
                  defaultMessage="Be sure to check out our size guide to find your best fit."
                />
              </SXFSizeGuideCalloutWrapper>
            )}
            <SizeGuide
              builderSizeGuide={builderSizeGuide}
              isScrubs={isScrubs}
              categoryIdList={categoryIdList}
              showFitGuide={showFitGuide}
              tagIdList={tagIdList}
            />
            <ProductDetailOtherSelectors
              disableAllOptions={productDetail.isAssortmentTestProductDisabled}
            />
            {showEmbroiderySelector && <ProductDetailEmbroiderySelector />}
            <ProductDetailQuantitySelector />
            {isMobile && (
              <>
                <ProductDetailPrice />
                <Separator thickness={1} separatorStyle={separatorStyle} />
              </>
            )}
          </ItemWrapper>
        </ScrollOffset>
      )}
      <HotComment product={product} />
    </OptionLabelProvider>
  );
}

ProductDetails.propTypes = {
  productDetail: PropTypes.object,
  productRef: PropTypes.any,
  relatedProductsLoading: PropTypes.bool,
};
